import React, { useEffect, useState } from 'react'
import { ProjectProps } from '../Project'
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Loading from '../../../components/Loading';
import MainBlock from '../../../components/MainBlock';
import { TbExternalLink } from 'react-icons/tb';
import { StorageModel } from '../../../utils/interfaces';
import { getStandardTime, isValidServiceName, useAsyncEffect, useEventListener } from '../../../utils/functions';
import { createItem, deleteItems, getItems } from '../../../api/API';
import { tables } from '../../../utils/constants';
import Table from '../../../components/Table';
import AlertWrapper from '../../../components/AlertWrapper';
import Alert from '../../../components/Alert';
import { Input } from '../../../components/Input';
import { Title } from '../../../components/General';

const Storage = (props: ProjectProps) => {
  
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [storages, setStorages] = useState<StorageModel[]>([]);
  const [stInfos, setStInfos] = useState<Record<string, any>[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMetaLoading, setIsMetaLoading] = useState(true);
  const [showDeleteStoragesAlert, setShowDeleteStoragesAlert] = useState(false);
  const [showAddStorageAlert, setShowAddStorageAlert] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newStorageName, setNewStorageName] = useState('');

  // event handlers
  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.add-storage-button') && !target.closest('.add-storage-alert')) {
      setShowAddStorageAlert(false);
    }
    if (!target.closest('.delete-storages-button') && !target.closest('.delete-storages-alert')) {
      setShowDeleteStoragesAlert(false);
    }
  })

  useEffect(() => {
    setNewStorageName('');
  }, [showAddStorageAlert]);

  // update metadata when storages change
  useAsyncEffect(async () => {
    let filtered: Record<string, any>[] = [];
    for (const st of storages) {
      filtered.push({ [t('table-attr.general.name')]: st.name, [t('table-attr.general.created-at')]: getStandardTime(new Date(st.createdAt)) });
    }
    setStInfos(filtered);
    if (!isLoading) setIsMetaLoading(false);
  }, [storages]);

  useAsyncEffect(async () => {
    const stdata = (await getItems(tables.storages, { 'projectId': props.project.id })).items || [];
    setStorages(stdata);
    setIsLoading(false);
  }, [props.project.id]);

  // add new storage
  const addNewStorage = async () => {
    setIsAdding(true);
    const newStorage: StorageModel = { id: `${props.project.id}_${newStorageName}`, name: newStorageName, userId: props.user!.id, projectId: props.project.id, description: '', adminIds: [{ userId: props.user!.id, access: 'full_access' }], createdAt: new Date().toISOString(), lastUpdate: new Date().toISOString() };
    await createItem(tables.storages, newStorage);
    setStorages(prev => [...prev, newStorage]);
    setShowAddStorageAlert(false);
    setIsAdding(false);
  }

  // delete storages
  const deleteStorages = async () => {
    setIsDeleting(true);
    await deleteItems(tables.storages, selected.map(name => `${props.project.id}_${name}`));
    setStorages(storages.filter(st => !selected.includes(st.name)));
    setSelected([]);
    setIsDeleting(false);
    setShowDeleteStoragesAlert(false);
  }
  
  return (
    isMetaLoading ? <main className='flex items-center justify-center flex-1'><Loading solid size={8} /></main>
      : <>
        <div className='flex-1 flex flex-col p-4 md:px-8 overflow-auto'>
          <div className='flex items-center justify-between gap-4'>
            <Title>{t('sidebar-menu.project.storage')}</Title>
          </div>
          <div className='flex-1 overflow-auto mt-4 flex flex-col'>
            <MainBlock extraclass='flex-1 flex flex-col overflow-auto'>
              <div className='flex items-center justify-between'>
                <div>
                  <div className='text-sm md:text-base font-bold'>{t('project.storage.all-storages')} ({stInfos.length})</div>
                </div>
                <div className='gap-4 flex my-1 flex-row'>
                  {stInfos.length > 0 && <button onClick={() => setShowDeleteStoragesAlert(true)} disabled={selected.length === 0} className='delete-storages-button bg-gray-100 disabled:opacity-30 rounded-lg p-2 px-4 text-xs'>{t('project.storage.delete-storages')}</button>}
                  <button onClick={() => setShowAddStorageAlert(true)} className='add-storage-button bg-primary text-white rounded-lg p-2 px-4 text-xs'>{t('project.storage.add-storage')}</button>
                </div>
              </div>
              <div className='flex flex-col flex-1 overflow-auto mt-2'>
                <Table
                  data={stInfos}
                  setData={setStInfos}
                  selected={selected}
                  setSelected={setSelected}
                  attrKey={t('table-attr.general.name')}
                  isKeyBotton
                  onClick={(e, item) => navigate(`/project/${props.project.id}/storage/${item[t('table-attr.general.name')]}`)}
                  attributes={[{ name: t('table-attr.general.name'), type: 'string' }, { name: t('table-attr.general.created-at'), type: 'string' }]}
                />
                {stInfos.length === 0 && <div className='flex-1 flex items-center justify-center font-light text-sm'>{t('project.storage.no-storage')}</div>}
              </div>
            </MainBlock>
          </div>
        </div>


        {/* delete storages */}
        {showDeleteStoragesAlert && <AlertWrapper>
          <Alert
            extraclass='delete-storages-alert'
            title={t('project.storage.delete-storages')}
            maxWidth={480}
            onClose={() => setShowDeleteStoragesAlert(false)}
            body={<div className='mx-1'>
              <div>{t('project.storage.delete-storage-message')}</div>
            </div>}
            bottom={<div className='flex'>
              <button onClick={deleteStorages} disabled={selected.length === 0 || isDeleting} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{isDeleting ? <Loading /> : t('general.delete')}</button>
            </div>}
          />
        </AlertWrapper>}

        {/* add storage */}
        {showAddStorageAlert && <AlertWrapper>
          <Alert
            extraclass='add-storage-alert'
            title={t('project.storage.add-storage')}
            maxWidth={400}
            onClose={() => setShowAddStorageAlert(false)}
            body={<div className='flex flex-col gap-2'>
              <div>
                <label className='text-xs text-gray-400 m-1'>{t('general.name')}<span className='text-xs ml-2'>({t('home.console.project.new-project.name-description')})</span></label>
                <Input autoFocus value={newStorageName} onChange={(e) => setNewStorageName(e.target.value)} />
                {storages.find(p => p.name === newStorageName) && <p className='text-xs text-red-400 m-1'>{t('home.console.project.new-project.name-exists')}</p>}
              </div>
            </div>}
            bottom={<div>
              <div className='flex'><button onClick={addNewStorage} disabled={isAdding || !!storages.find(p => p.name === newStorageName) || !isValidServiceName(newStorageName)} className='flex items-center justify-center bg-primary disabled:bg-gray-300 text-white px-8 py-2 rounded-lg text-sm'>{isAdding ? <Loading /> : t('general.add')}</button></div>
            </div>}
          />
        </AlertWrapper>}
      </>
  )
}

export default Storage